import type { BrickRegion, BrickType, MasterImage, Pixels, Rect, Stroke } from "./types";

export const TARGET_PX_PER_MM = 2;

export interface RenderInput {
  image: Pixels;
  rect: Rect;
  alpha: Uint8Array;
  sourcePxPerMm: number;
}

export const renderBrick = ({ image, rect, alpha, sourcePxPerMm }: RenderInput): Pixels => {
  const scale = Math.min(1, TARGET_PX_PER_MM / sourcePxPerMm);
  const width = Math.max(1, Math.round(rect.width * scale));
  const height = Math.max(1, Math.round(rect.height * scale));
  const sums = new Float64Array(width * height * 4);
  const counts = new Uint32Array(width * height);

  for (let y = 0; y < rect.height; y++) {
    const oy = Math.min(height - 1, Math.floor((y * height) / rect.height));
    for (let x = 0; x < rect.width; x++) {
      const ox = Math.min(width - 1, Math.floor((x * width) / rect.width));
      const o = oy * width + ox;
      const s = ((rect.y + y) * image.width + rect.x + x) * 4;
      const a = alpha[y * rect.width + x];
      for (let c = 0; c < 3; c++) sums[o * 4 + c] += image.data[s + c] * a;
      sums[o * 4 + 3] += a;
      counts[o]++;
    }
  }

  const data = new Uint8ClampedArray(width * height * 4);
  for (let o = 0; o < width * height; o++) {
    const alphaSum = sums[o * 4 + 3];
    if (alphaSum === 0) continue;
    for (let c = 0; c < 3; c++) data[o * 4 + c] = Math.round(sums[o * 4 + c] / alphaSum);
    data[o * 4 + 3] = Math.round(alphaSum / counts[o]);
  }
  return { width, height, data };
};

export interface RenderParams {
  masterImage: string;
  rotation: number;
  pxPerMm: number;
  maskVersion: number;
  tolerance: number;
  region: Rect & { type: BrickType; strokes: Stroke[] };
}

const cyrb53 = (text: string) => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hex = (n: number) => (n >>> 0).toString(16).padStart(8, "0");
  return hex(h2) + hex(h1);
};

export const renderHash = ({ masterImage, rotation, pxPerMm, maskVersion, tolerance, region }: RenderParams) =>
  cyrb53(
    JSON.stringify([
      masterImage,
      rotation,
      pxPerMm,
      maskVersion,
      tolerance,
      region.x,
      region.y,
      region.width,
      region.height,
      region.type,
      region.strokes.map((s) => [s.mode, s.radius, s.points]),
    ])
  );

export const regionRenderHash = (masterImage: MasterImage, region: BrickRegion): string | null =>
  masterImage.pxPerMm == null
    ? null
    : renderHash({
        masterImage: masterImage._id,
        rotation: masterImage.rotation,
        pxPerMm: masterImage.pxPerMm,
        maskVersion: masterImage.maskVersion,
        tolerance: region.tolerance ?? masterImage.tolerance,
        region,
      });
